import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { MaskedLines, FadeUp } from "@/components/Reveal";
import { ASSETS } from "@/data/site";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const badgeY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section ref={ref} className="relative overflow-hidden bg-ivory pt-32 lg:pt-40" data-testid="hero-section">
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-16 px-6 pb-24 lg:grid-cols-12 lg:px-10 lg:pb-32">
        <div className="lg:col-span-7">
          <FadeUp delay={0.1} y={12}>
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-sienna" data-testid="hero-overline">
              Traditional · Womb · Postpartum
            </p>
          </FadeUp>
          <MaskedLines
            delay={0.25}
            className="mt-8 font-serif text-5xl font-light leading-[1.02] tracking-tight text-espresso sm:text-6xl lg:text-[5.4rem]"
            lines={[
              <>The first</>,
              <><span className="italic text-sienna">forty days,</span></>,
              <>held gently.</>,
            ]}
          />
          <FadeUp delay={0.8}>
            <p className="mt-8 max-w-md text-base leading-relaxed text-cocoa lg:text-lg" data-testid="hero-subcopy">
              Come sit awhile… Our signature gathering is preparation for the fourth
              trimester — a time for mothers and their people to remember the traditional
              ways of tending the sacred weeks before and after birth. And when you come into your
              time of rest and receiving, I bring that care into your home: nourishing
              warm meals, quiet companionship, steady hands, belly binding, and
              time-honored rituals passed down through generations of women.
            </p>
          </FadeUp>
          <FadeUp delay={1}>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link
                to="/book?service=planning-for-the-4th-trimester"
                data-testid="hero-book-button"
                className="rounded-full bg-sienna px-8 py-3.5 text-sm font-medium uppercase tracking-[0.12em] text-ivory transition-colors hover:bg-sienna-dark"
              >
                Join the class
              </Link>
              <button
                onClick={() => document.getElementById("services")?.scrollIntoView({ behavior: "smooth" })}
                data-testid="hero-explore-button"
                className="rounded-full border border-espresso/30 px-8 py-3.5 text-sm font-medium uppercase tracking-[0.12em] text-espresso transition-colors hover:bg-espresso hover:text-ivory"
              >
                Explore all care
              </button>
            </div>
          </FadeUp>
        </div>

        <div className="relative lg:col-span-5">
          <motion.div style={{ y: imgY }} className="relative">
            <motion.div
              initial={{ clipPath: "inset(100% 0 0 0)" }}
              animate={{ clipPath: "inset(0% 0 0 0)" }}
              transition={{ duration: 1.4, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
              className="clip-arch-full overflow-hidden border border-espresso/15"
              data-testid="hero-logo-frame"
            >
              <img
                src={ASSETS.logo}
                alt="inbornbirth — the mother-goddess mark"
                className="aspect-[4/5] w-full object-cover"
              />
            </motion.div>
          </motion.div>
          <motion.div style={{ y: badgeY }} className="absolute -bottom-8 -left-6 lg:-left-12">
            <motion.div
              initial={{ opacity: 0, scale: 0.6 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.9, delay: 1.2, ease: [0.16, 1, 0.3, 1] }}
              className="h-28 w-28 overflow-hidden rounded-full ring-4 ring-ivory lg:h-32 lg:w-32"
              data-testid="hero-badge"
            >
              <motion.img
                src={ASSETS.badge}
                alt="Innate Postpartum Care — Certified Practitioner"
                className="h-full w-full object-cover"
                animate={{ rotate: 360 }}
                transition={{ repeat: Infinity, duration: 40, ease: "linear" }}
              />
            </motion.div>
          </motion.div>
        </div>
      </div>

      <FadeUp delay={1.3} className="mx-auto max-w-7xl px-6 pb-10 lg:px-10">
        <div className="flex items-center gap-3 text-xs font-medium uppercase tracking-[0.25em] text-cocoa/60">
          <ArrowDown className="h-4 w-4 animate-bounce" strokeWidth={1.5} />
          <span>Scroll slowly</span>
        </div>
      </FadeUp>
    </section>
  );
}
